import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useComponentI18n } from '@rdrive/i18n';
import { tokens } from '@rdrive/theme';
import messages from './messages/index.js';
import marketingItems from './marketingItems.js';

// Marketing panel geometry (showMarketingPanel) — lifted from issue-analysis's copy.
// The item pages are cross-origin, so they cannot be measured; they may report their
// height by postMessage instead (see onItemMessage).
const DEFAULT_ITEM_HEIGHT = 600;
const ITEM_HEADER_HEIGHT = 57; // header row + divider above the item page

export default (props, { emit }) => {
  // LOCAL composer with the warn flags off — see @rdrive/i18n for why the flags are
  // assigned rather than passed.
  const { t } = useComponentI18n(messages);

  const show = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
  });

  // Shrink the title font just enough to keep the whole message on one line.
  // The title keeps its CSS clamp() size as the maximum; only longer names scale down.
  // `titleEl` is a TEMPLATE ref (`ref="titleEl"` in component.vue) — it only binds
  // because it is returned under that exact name.
  const titleEl = ref(null);
  let titleResizeObserver = null;

  const fitTitle = () => {
    const el = titleEl.value;
    if (!el) return;
    el.style.fontSize = '';
    if (el.scrollWidth <= el.clientWidth) return;
    const base = parseFloat(window.getComputedStyle(el).fontSize);
    // 0.995: scrollWidth/clientWidth are rounded to integers, leave sub-pixel slack
    el.style.fontSize = (base * (el.clientWidth / el.scrollWidth) * 0.995) + 'px';
  };

  // The title element mounts late (dialog opens, then projectName arrives), so react to
  // the ref itself rather than fitting once on mount
  watch(titleEl, (el) => {
    if (titleResizeObserver) {
      titleResizeObserver.disconnect();
      titleResizeObserver = null;
    }
    if (el) {
      titleResizeObserver = new ResizeObserver(fitTitle);
      titleResizeObserver.observe(el.parentElement);
      fitTitle();
      // The Adobe font loads async and is wider/narrower than the fallback — re-measure
      document.fonts?.ready?.then(fitTitle);
    }
  });

  watch(() => props.projectName, () => nextTick(fitTitle));

  onBeforeUnmount(() => {
    if (titleResizeObserver) titleResizeObserver.disconnect();
  });

  const showToast = ref(false);
  const showErrorToast = ref(false);
  const loadingData = ref(false);

  // Compute the progress percentage
  const progressValue = computed(() => {
    return (props.apiProgress / props.totalApiRequests) * 100;
  });

  // Requires the embedding SPA to delegate clipboard access on the iframe tag
  // (<iframe allow="clipboard-write">) — without it, cross-origin iframes are
  // denied by permissions policy and writeText rejects.
  const copyQueryString = () => {
    emit('copyQueryString');

    navigator.clipboard.writeText(props.queryString)
      .then(() => { showToast.value = true; })
      .catch((err) => {
        console.error('Copy failed:', err);
        showErrorToast.value = true;
      });
  };

  const loadDashboard = () => {
    loadingData.value = true;
    emit('load');
  };

  // --- "Discover what's new from RDrive" panel (opt-in: showMarketingPanel) ----------
  // Everything below is inert with the prop off: the template renders none of it (every
  // panel element sits under v-if="showMarketingPanel"), `cardClass`/`cardStyle` are null
  // (so the v-card renders exactly the classes and style it always did), `activeId` stays
  // null (so `persistent` and click-outside behave as before) and no listener is added.
  //
  // Layout with it on: the hero and the cards stay in normal flow and the item page panel
  // sits absolutely on top, off to the right. Opening an item slides all three left by one
  // card width (the "carousel") and pins the card to the item page's height.

  // The open item's id (null = the welcome view). An id, not the item object, so a locale
  // switch re-translates the header of an open item too.
  const activeId = ref(null);
  // How much of the open item page the card shows (px) — `crop`/`height` on open, then
  // whatever the page reports by postMessage.
  const cropHeight = ref(DEFAULT_ITEM_HEIGHT);

  // Only evaluated with the panel on (only the panel's template reads it).
  const panelItems = computed(() => marketingItems.map((item) => ({
    ...item,
    title: t(item.titleKey),
    description: t(item.descriptionKey),
    height: item.height || DEFAULT_ITEM_HEIGHT,
  })));
  const activeItem = computed(() => panelItems.value.find((item) => item.id === activeId.value) || null);

  const cardClass = computed(() => (props.showMarketingPanel
    ? {
        'rdrive-welcome-card--marketing': true,
        'rdrive-welcome-card--item-open': activeId.value !== null,
      }
    : null));

  // The card grows to fit the open item page; the welcome view sizes to its content.
  // The panel's colours reach style.css as custom properties (no literal in the CSS).
  const cardStyle = computed(() => (props.showMarketingPanel
    ? {
        height: activeId.value !== null ? `${cropHeight.value + ITEM_HEADER_HEIGHT}px` : 'auto',
        '--rdrive-welcome-panel-bg': tokens.welcome.panelBackground,
        '--rdrive-welcome-panel-heading': tokens.welcome.panelHeading,
        '--rdrive-welcome-panel-title': tokens.welcome.panelTitle,
        '--rdrive-welcome-panel-text': tokens.welcome.panelText,
      }
    : null));

  const openMarketingItem = (item) => {
    cropHeight.value = item.crop || item.height || DEFAULT_ITEM_HEIGHT;
    activeId.value = item.id;
    emit('marketingItemOpen', { id: item.id, url: item.url });
  };

  // The back button, and a click outside the card while an item is open (the dialog is
  // persistent then, so that click only returns to the welcome view).
  const closeMarketingItem = () => {
    if (activeId.value !== null) activeId.value = null;
  };

  // Load events per item: 1 = the background preload; any later one means the user
  // navigated inside the item page — open its real page in a new tab (if it has one)
  // and put the popup back.
  const frameLoads = new Map();
  const onItemFrameLoad = (item, event) => {
    const count = (frameLoads.get(item.id) || 0) + 1;
    frameLoads.set(item.id, count);
    if (count === 1) return;

    if (item.targetUrl) {
      window.open(item.targetUrl, '_blank');
      emit('marketingItemVisit', { id: item.id, url: item.url, targetUrl: item.targetUrl });
    }
    const frame = event?.target;
    if (frame) {
      frameLoads.set(item.id, 0);
      frame.src = item.url;
    }
  };

  // The item pages may report their content height: { type: 'rdrive:iframe-height', height }.
  // Accepted from rdrive.io (and its subdomains) only.
  const onItemMessage = (e) => {
    let host;
    try { host = new URL(e.origin).hostname; } catch { return; }
    if (host !== 'rdrive.io' && !host.endsWith('.rdrive.io')) return;
    if (e.data?.type !== 'rdrive:iframe-height') return;
    const h = Number(e.data.height);
    if (Number.isFinite(h) && h > 0) cropHeight.value = Math.ceil(h);
  };

  // The window listener exists only while the panel is on.
  let listening = false;
  const listenForItemHeights = (on) => {
    if (on === listening) return;
    listening = on;
    if (on) window.addEventListener('message', onItemMessage);
    else window.removeEventListener('message', onItemMessage);
  };
  onMounted(() => listenForItemHeights(props.showMarketingPanel));
  watch(() => props.showMarketingPanel, (on) => listenForItemHeights(on));
  onBeforeUnmount(() => listenForItemHeights(false));

  return {
    t,
    show,
    titleEl,
    showToast,
    showErrorToast,
    loadingData,
    progressValue,
    copyQueryString,
    loadDashboard,
    activeId,
    activeItem,
    cropHeight,
    panelItems,
    cardClass,
    cardStyle,
    openMarketingItem,
    closeMarketingItem,
    onItemFrameLoad,
  };
};
